import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function WatchlistPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('watches');
  const [watches, setWatches] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      API.get('/watchlist/watches'),
      API.get('/watchlist/searches'),
    ]).then(([w, s]) => {
      setWatches(w.data.watches || []);
      setSearches(s.data.searches || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const removeWatch = async (listingId) => {
    await API.post(`/watchlist/watches/${listingId}`);
    setWatches(p => p.filter(w => w.listing_id !== listingId));
  };

  const removeSearch = async (id) => {
    await API.delete(`/watchlist/searches/${id}`);
    setSearches(p => p.filter(s => s.id !== id));
  };

  const toggleSearchEmail = async (s) => {
    const { data } = await API.patch(`/watchlist/searches/${s.id}`, { notifyEmail: !s.notify_email });
    setSearches(p => p.map(x => x.id === s.id ? { ...x, notify_email: data.search.notify_email } : x));
  };

  if (loading) return <div style={{ textAlign:'center', padding:80 }}><div style={{ fontSize:40 }} className="float-anim">🍒</div></div>;

  return (
    <div style={{ maxWidth:900, margin:'0 auto', padding:'40px 24px' }}>
      <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:6 }}>🔔 Watchlist & Alerts</h1>
      <p style={{ color:'var(--text-soft)', fontSize:14, marginBottom:28 }}>Get notified when prices drop or new listings match your searches.</p>

      <div className="tab-bar" style={{ marginBottom:28 }}>
        {[['watches','🔕 Price Alerts'],['searches','🔍 Saved Searches']].map(([id, label]) => (
          <button key={id} className={`tab-btn ${tab===id?'active':''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* Price-drop watches */}
      {tab === 'watches' && (
        <div>
          {watches.length === 0
            ? (
              <div style={{ textAlign:'center', padding:'60px 20px', color:'var(--text-soft)' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🔕</div>
                <p style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>No price alerts yet</p>
                <p style={{ fontSize:13, marginBottom:20 }}>Click 🔕 on any listing to get notified when the price drops.</p>
                <button className="btn btn-cherry btn-sm" onClick={() => navigate('/browse')}>Browse Listings</button>
              </div>
            )
            : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {watches.map(w => {
                  const dropped = parseFloat(w.price) < parseFloat(w.price_at_watch);
                  return (
                    <div key={w.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', gap:14, alignItems:'center' }}>
                      <div style={{ width:56, height:56, borderRadius:8, overflow:'hidden', flexShrink:0, background:'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, cursor:'pointer' }}
                        onClick={() => navigate(`/listing/${w.slug || w.listing_id}`)}>
                        {w.image_url ? <img src={`${API_BASE}${w.image_url}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '📦'}
                      </div>
                      <div style={{ flex:1, cursor:'pointer' }} onClick={() => navigate(`/listing/${w.slug || w.listing_id}`)}>
                        <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{w.title}</div>
                        <div style={{ fontSize:12, color:'var(--text-soft)' }}>{w.seller_first} {w.seller_last} · {w.listing_type}</div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        {dropped && (
                          <div style={{ fontSize:10, fontWeight:700, color:'white', background:'var(--green)', padding:'2px 8px', borderRadius:100, marginBottom:4, display:'inline-block' }}>
                            Price dropped!
                          </div>
                        )}
                        <div style={{ fontWeight:700, fontSize:16, color: dropped ? 'var(--green)' : 'var(--text-primary)' }}>
                          {w.price ? `$${parseFloat(w.price).toFixed(2)}` : 'Free'}
                        </div>
                        {dropped && (
                          <div style={{ fontSize:11, color:'var(--text-soft)', textDecoration:'line-through' }}>
                            was ${parseFloat(w.price_at_watch).toFixed(2)}
                          </div>
                        )}
                        {!dropped && w.price_at_watch && (
                          <div style={{ fontSize:11, color:'var(--text-soft)' }}>when watched: ${parseFloat(w.price_at_watch).toFixed(2)}</div>
                        )}
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{ flexShrink:0 }} onClick={() => removeWatch(w.listing_id)} title="Remove watch">✕</button>
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
      )}

      {/* Saved searches */}
      {tab === 'searches' && (
        <div>
          {searches.length === 0
            ? (
              <div style={{ textAlign:'center', padding:'60px 20px', color:'var(--text-soft)' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
                <p style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>No saved searches</p>
                <p style={{ fontSize:13, marginBottom:20 }}>Search for anything and click "Save Search" to get email alerts when new listings match.</p>
                <button className="btn btn-cherry btn-sm" onClick={() => navigate('/browse')}>Browse Listings</button>
              </div>
            )
            : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {searches.map(s => {
                  const filters = s.filters || {};
                  const parts = [s.query, filters.type, filters.category, filters.condition, filters.minPrice && `$${filters.minPrice}+`, filters.maxPrice && `up to $${filters.maxPrice}`].filter(Boolean);
                  return (
                    <div key={s.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:14 }}>
                      <div style={{ fontSize:24 }}>🔍</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{s.name}</div>
                        <div style={{ fontSize:12, color:'var(--text-soft)' }}>{parts.join(' · ') || 'All listings'}</div>
                        {s.last_notified_at && <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:2 }}>Last alert: {new Date(s.last_notified_at).toLocaleDateString()}</div>}
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize:12 }}
                        onClick={() => { const p = new URLSearchParams(); if(s.query) p.set('q', s.query); if(filters.type) p.set('type', filters.type); navigate(`/browse?${p}`); }}>
                        View results →
                      </button>
                      <button className={`btn btn-sm ${s.notify_email ? 'btn-cherry' : 'btn-ghost'}`} style={{ fontSize:11, flexShrink:0 }}
                        onClick={() => toggleSearchEmail(s)} title={s.notify_email ? 'Emails on — click to pause' : 'Emails off — click to enable'}>
                        {s.notify_email ? '🔔 Alerts on' : '🔕 Alerts off'}
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ flexShrink:0 }} onClick={() => removeSearch(s.id)} title="Delete saved search">✕</button>
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
      )}
    </div>
  );
}
